/**
 * Entry-fee payment modal — HTML/CSS backed so it matches the dark
 * glassy wallet-pill aesthetic. Driven by a step state machine:
 *
 *   connect → choose → waiting → (success | failed)
 *
 * PerkSelectScene orchestrates the steps via the public API. Payment
 * logic (signing, verifying) lives in payments/entry-fee + server
 * routes, not here.
 */

export type PaymentStep = 'connect' | 'choose' | 'waiting' | 'success' | 'failed';
export type CoinKind = 'usdc' | 'sol' | 'native';
export type StakeMultiplierValue = 1 | 2 | 5 | 10;

type StatusKind = 'info' | 'error' | 'success';

export interface PaymentOverlayCallbacks {
  /** Emitted when the user clicks CONNECT WALLET on the connect step. */
  onConnect?: () => void;
  /** Emitted when the user picks SOL or USDC on the choose step. */
  onCoinChosen?: (kind: CoinKind) => void;
  /** Emitted when the user changes the stake multiplier. The scene should
   *  re-fetch the quote and call `setAmounts` with the new labels. */
  onStakeChanged?: (multiplier: StakeMultiplierValue) => void;
  /** Emitted when the user hits CANCEL (any step). */
  onCancel?: () => void;
}

interface StepCopy { kicker: string; title: string; description: string; tip?: string }

const COPY: Record<PaymentStep, StepCopy> = {
  connect: {
    kicker: 'WALLET',
    title: 'Connect your Solana wallet',
    description: 'Phantom or Solflare both work. We need a one-shot signature so the entry fee can be sent on-chain.',
    tip: 'Tip: your wallet stays in your control — we never see your private key.',
  },
  choose: {
    kicker: 'ENTRY FEE',
    title: 'Choose how you want to pay',
    description: 'The entry fee funds the match prize pool. Pick whichever currency you prefer.',
    tip: 'Tip: <strong>USDC</strong> keeps the value stable. <strong>${NATIVE}</strong> is the game\'s native token — paid back to winners.',
  },
  waiting: {
    kicker: 'PAYMENT',
    title: 'Confirming on Solana',
    description: 'This may take ~10–15 seconds while the network finalizes the transfer. Don\'t close the window.',
    tip: '',
  },
  success: {
    kicker: 'PAYMENT',
    title: 'Payment confirmed',
    description: 'You\'re in. Dealing your perks now…',
    tip: '',
  },
  failed: {
    kicker: 'PAYMENT',
    title: 'Payment failed',
    description: 'The transaction didn\'t go through. You can close this and try again.',
    tip: '',
  },
};

export class PaymentOverlay {
  private root: HTMLElement | null;
  private stepConnect: HTMLElement | null;
  private stepChoose: HTMLElement | null;
  private stepWaiting: HTMLElement | null;
  private coinUsdc: HTMLElement | null;
  private coinSol: HTMLElement | null;
  private coinNative: HTMLElement | null;
  private usdcAmountEl: HTMLElement | null;
  private solAmountEl: HTMLElement | null;
  private nativeAmountEl: HTMLElement | null;
  private nativeNameEl: HTMLElement | null;
  private kickerEl: HTMLElement | null;
  private titleEl: HTMLElement | null;
  private descriptionEl: HTMLElement | null;
  private tipEl: HTMLElement | null;
  private statusEl: HTMLElement | null;
  private detailEl: HTMLElement | null;
  private progressEl: HTMLElement | null;
  private progressLabelEl: HTMLElement | null;
  private cancelBtn: HTMLButtonElement | null;
  private primaryBtn: HTMLButtonElement | null;
  private devnetWarningEl: HTMLElement | null;
  private stakeButtons: HTMLButtonElement[] = [];
  private cb: PaymentOverlayCallbacks = {};
  private currentStep: PaymentStep = 'connect';
  private selectedStake: StakeMultiplierValue = 1;
  /** Native token symbol, resolved from `/api/payments/quote` via
   *  setNativeAvailable. Interpolated into the choose-step tip ({NATIVE})
   *  so we never show the legacy hardcoded "$SNK". */
  private nativeSymbol: string = 'PMS';

  constructor() {
    this.root            = document.getElementById('payment-overlay');
    this.stepConnect     = document.getElementById('pay-step-connect');
    this.stepChoose      = document.getElementById('pay-step-choose');
    this.stepWaiting     = document.getElementById('pay-step-waiting');
    this.coinUsdc        = document.getElementById('pay-coin-usdc');
    this.coinSol         = document.getElementById('pay-coin-sol');
    this.coinNative      = document.getElementById('pay-coin-native');
    this.usdcAmountEl    = document.getElementById('pay-usdc-amount');
    this.solAmountEl     = document.getElementById('pay-sol-amount');
    this.nativeAmountEl  = document.getElementById('pay-native-amount');
    this.nativeNameEl    = document.getElementById('pay-coin-native-name');
    this.kickerEl        = document.getElementById('pay-kicker');
    this.titleEl         = document.getElementById('pay-title');
    this.descriptionEl   = document.getElementById('pay-description');
    this.tipEl           = document.getElementById('pay-tip');
    this.statusEl        = document.getElementById('pay-status');
    this.detailEl        = document.getElementById('pay-detail');
    this.progressEl      = document.getElementById('pay-progress');
    this.progressLabelEl = document.getElementById('pay-progress-label');
    this.cancelBtn       = document.getElementById('pay-btn-cancel') as HTMLButtonElement | null;
    this.primaryBtn      = document.getElementById('pay-btn-primary') as HTMLButtonElement | null;
    this.devnetWarningEl = document.getElementById('pay-devnet-warning');

    this.cancelBtn?.addEventListener('click', () => this.cb.onCancel?.());
    this.primaryBtn?.addEventListener('click', () => {
      if (this.currentStep === 'connect') this.cb.onConnect?.();
    });
    this.coinUsdc?.addEventListener('click',  () => this.cb.onCoinChosen?.('usdc'));
    this.coinSol?.addEventListener('click',   () => this.cb.onCoinChosen?.('sol'));
    this.coinNative?.addEventListener('click', () => this.cb.onCoinChosen?.('native'));

    // Wire the stake-multiplier picker. Buttons live inside #pay-stake-buttons
    // with data-stake="1|2|5|10". Clicking one becomes the active selection
    // and fires onStakeChanged so the scene can re-fetch the quote.
    const stakeRoot = document.getElementById('pay-stake-buttons');
    if (stakeRoot) {
      this.stakeButtons = Array.from(stakeRoot.querySelectorAll<HTMLButtonElement>('button[data-stake]'));
      for (const btn of this.stakeButtons) {
        btn.addEventListener('click', () => {
          const raw = Number(btn.dataset.stake);
          if (raw !== 1 && raw !== 2 && raw !== 5 && raw !== 10) return;
          if (raw === this.selectedStake) return; // no-op
          this.setStakeMultiplier(raw);
          this.cb.onStakeChanged?.(raw);
        });
      }
    }
  }

  /** Show or hide the native-token (PMS) card. Called by PerkSelectScene
   *  after `/api/payments/quote` returns: if the response contains a
   *  `native` block (mainnet only), unhide and label; otherwise stay
   *  hidden so devnet keeps its 2-card layout. */
  setNativeAvailable(visible: boolean, opts: { symbol?: string; label?: string } = {}): void {
    if (!this.coinNative) return;
    this.coinNative.style.display = visible ? '' : 'none';
    if (visible) {
      if (opts.symbol) {
        this.nativeSymbol = opts.symbol;
        if (this.nativeNameEl) this.nativeNameEl.textContent = opts.symbol;
        // The symbol can arrive after the choose step is already on
        // screen, so re-render the tip to pick up the resolved name.
        if (this.currentStep === 'choose') this.renderTip('choose');
      }
      if (opts.label  && this.nativeAmountEl) this.nativeAmountEl.textContent = opts.label;
    }
  }

  /** Open the modal, starting on the requested step. */
  show(opts: {
    step: PaymentStep;
    usdcLabel?: string;
    solLabel?: string;
    /** Show the yellow "make sure your wallet is on Devnet" banner.
     *  Caller passes true on devnet builds — Phantom doesn't auto-switch
     *  network, and the only way for users to know is to be told. */
    devnetWarning?: boolean;
    callbacks?: PaymentOverlayCallbacks;
  }): void {
    this.cb = opts.callbacks ?? {};
    if (this.usdcAmountEl && opts.usdcLabel) this.usdcAmountEl.textContent = opts.usdcLabel;
    if (this.solAmountEl  && opts.solLabel)  this.solAmountEl.textContent  = opts.solLabel;
    this.setDevnetWarning(opts.devnetWarning ?? false);
    this.setStep(opts.step);
    this.root?.classList.add('visible');
  }

  /** Toggle the devnet warning banner. Visible only during connect/choose
   *  steps — once we're in `waiting` the user has already committed. */
  setDevnetWarning(on: boolean): void {
    if (!this.devnetWarningEl) return;
    this.devnetWarningEl.classList.toggle('visible', on);
  }

  /** Flip between steps without hiding the panel. */
  setStep(step: PaymentStep): void {
    this.currentStep = step;
    if (this.stepConnect) this.stepConnect.style.display = step === 'connect' ? 'flex' : 'none';
    // Choose step wraps the stake picker + coin grid in a flex column.
    if (this.stepChoose)  this.stepChoose.style.display  = step === 'choose'  ? 'flex' : 'none';
    if (this.stepWaiting) this.stepWaiting.style.display = step === 'waiting' || step === 'success' || step === 'failed' ? 'flex' : 'none';

    // Hide the devnet banner once the user has committed — no point
    // warning them after they've already signed.
    if (step === 'success' || step === 'failed') this.setDevnetWarning(false);

    // Header copy per step
    const copy = COPY[step];
    if (this.kickerEl)      this.kickerEl.textContent       = copy.kicker;
    if (this.titleEl)       this.titleEl.textContent        = copy.title;
    if (this.descriptionEl) this.descriptionEl.textContent  = copy.description;
    this.renderTip(step);

    if (step === 'waiting') {
      this.setProgress('indeterminate', '');
      this.setStatus('Waiting for signature…', 'info');
    } else if (step === 'success') {
      this.setProgress('done', '✓');
      this.setStatus('Payment confirmed', 'success');
    } else if (step === 'failed') {
      this.setProgress('done', '!');
    }
  }

  /** Render the per-step tip, interpolating {NATIVE} with the resolved
   *  token symbol so the legacy hardcoded "$SNK" never shows. */
  private renderTip(step: PaymentStep): void {
    if (!this.tipEl) return;
    const tip = COPY[step].tip;
    if (tip) {
      this.tipEl.innerHTML = tip.replace(/\{NATIVE\}/g, this.nativeSymbol);
      this.tipEl.style.display = '';
    } else {
      this.tipEl.style.display = 'none';
    }
  }

  /** Update the progress ring. `phase` ∈ [0..1] advances the determinate
   *  ring; omit for indeterminate (spinning). */
  setProgress(mode: 'indeterminate' | 'done' | number, label: string): void {
    if (!this.progressEl || !this.progressLabelEl) return;
    const fg = this.progressEl.querySelector<SVGCircleElement>('.ring-fg');
    if (mode === 'indeterminate') {
      this.progressEl.classList.add('indeterminate');
      if (fg) fg.style.strokeDashoffset = '';
    } else {
      this.progressEl.classList.remove('indeterminate');
      if (fg) {
        const pct = mode === 'done' ? 1 : Math.max(0, Math.min(1, mode));
        const CIRCUM = 251.32;
        fg.style.strokeDasharray = `${CIRCUM}`;
        fg.style.strokeDashoffset = `${CIRCUM * (1 - pct)}`;
      }
    }
    this.progressLabelEl.textContent = label;
  }

  setStatus(msg: string, kind: StatusKind = 'info', detail: string = ''): void {
    if (this.statusEl) {
      this.statusEl.textContent = msg;
      this.statusEl.classList.toggle('error', kind === 'error');
      this.statusEl.classList.toggle('success', kind === 'success');
    }
    if (this.detailEl) this.detailEl.textContent = detail;
  }

  /** Variant of `setStatus` that renders an anchor in the detail row.
   *  Use for actionable errors (e.g. "Not enough SNK" → link to pump.fun).
   *  The user clicks intentionally — no auto-popup, browsers don't block. */
  setStatusWithLink(
    msg: string,
    kind: StatusKind,
    detail: string,
    link: { href: string; label: string },
  ): void {
    if (this.statusEl) {
      this.statusEl.textContent = msg;
      this.statusEl.classList.toggle('error', kind === 'error');
      this.statusEl.classList.toggle('success', kind === 'success');
    }
    if (this.detailEl) {
      const a = document.createElement('a');
      a.href = link.href;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.textContent = link.label;
      a.className = 'pay-detail-link';
      this.detailEl.replaceChildren(
        document.createTextNode(detail ? detail + ' ' : ''),
        a,
      );
    }
  }

  /** Current step (used by callers that want to refresh labels without
   *  re-showing the panel from scratch). */
  currentStepName(): PaymentStep {
    return this.currentStep;
  }

  /** Update just the coin amount labels (e.g. after quote arrives). */
  setAmounts(opts: { usdcLabel?: string; solLabel?: string; nativeLabel?: string }): void {
    if (this.usdcAmountEl   && opts.usdcLabel)   this.usdcAmountEl.textContent   = opts.usdcLabel;
    if (this.solAmountEl    && opts.solLabel)    this.solAmountEl.textContent    = opts.solLabel;
    if (this.nativeAmountEl && opts.nativeLabel) this.nativeAmountEl.textContent = opts.nativeLabel;
  }

  /** Programmatically set the active stake-multiplier pill. Does NOT fire
   *  `onStakeChanged` (only user clicks do). Used by callers to mirror the
   *  resolved server multiplier or initialize from a saved preference. */
  setStakeMultiplier(multiplier: StakeMultiplierValue): void {
    this.selectedStake = multiplier;
    for (const btn of this.stakeButtons) {
      const raw = Number(btn.dataset.stake);
      btn.classList.toggle('active', raw === multiplier);
    }
  }

  /** Current stake multiplier the user has selected (pill highlighted). */
  currentStakeMultiplier(): StakeMultiplierValue {
    return this.selectedStake;
  }

  /** Disable / re-enable the stake picker. Useful during the brief moment
   *  between clicking a stake and the quote re-fetch landing — prevents
   *  rapid clicks from queueing stale requests. */
  setStakePickerEnabled(enabled: boolean): void {
    for (const btn of this.stakeButtons) btn.disabled = !enabled;
  }

  hide(): void {
    this.root?.classList.remove('visible');
    this.cb = {};
    // Reset to x1 so the next time the overlay opens the picker isn't
    // showing a stale selection from a previous attempt.
    this.setStakeMultiplier(1);
  }
}
